import { useStateStore } from '$tablet-app/src/app/stores/useStateStore';
import { getUserDataForChatContext } from '$tablet-app/src/app/users/functions';
import { append, appendArray } from '@akin-home/arrays';
import { ChatResponseOption, getCommandsFromChatItems } from '@akin-home/chat';
import {
  ChatApiResponse,
  ChatInputType,
  chatResponseIsDirectMatch,
  postChatMessage,
} from '@akin-home/chat/frontend';
import {
  StateUpdate,
  updateStateVariables,
} from '@akin-home/state-variables-frontend';
import { UserModel, getUserChatAppID, getUserChatID } from '@akin-home/user';
import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import {
  createInputChatItem,
  createOutputTextItem,
} from '../../ChatItem/functions';
import { ChatItem } from '../../ChatItem/types';
import { handleClosedFocusedChatCommand } from '../../commands/focusedChatCommands/focused_chat_commands';
import { createFollowUpCommandHandler } from '../../commands/handleFollowupInputCommand';
import { ChatCommand } from '../../commands/types';
import { filterInvalidChatItems, isOutputChatItem } from '../../functions';

/** We will use a zustand state store for ease */
export const useFocusedChatState = create<FocusedChatState>()(
  immer((set, get) => ({
    chat_items: [] as ChatItem[],

    async sendMessage(message, options) {
      const { session_id, user, addChatItem } = get();

      const context = options?.context ?? {};

      // There should be a user initialized before sending a message
      if (!user) return;

      const application_id = getUserChatAppID(user);

      if (!options?.silent) {
        addChatItem(createInputChatItem(message));
      }

      const response = await postChatMessage({
        message,
        message_type: options?.message_type || 'text',
        username: getUserChatID(user),
        session_id,
        application_id,
        context: {
          in_focused_chat: true,
          ...getUserDataForChatContext(user),
          ...context,
        },
      });

      get().processResponse(response, {
        direct_only: options?.message_type === 'labeled_intent',
      });
    },

    async sendBackgroundMessage(message, context) {
      get().sendMessage(message, { silent: true, context });
    },

    async sendLabeledMessage(message, context) {
      get().sendMessage(message, {
        silent: true,
        message_type: 'labeled_intent',
        context,
      });
    },

    processResponse(response, options) {
      const { addChatItem, processCommands } = get();

      if (!response.success) {
        get().addChatItem(
          createOutputTextItem(
            'Sorry, I am not able to respond at the moment. Please try again soon.'
          )
        );

        // Abort
        return;
      }

      // If it must be a direct match, ensure that it is
      if (options?.direct_only && !chatResponseIsDirectMatch(response)) {
        return;
      }

      const items = response.body;

      /** Update the session ID */
      set({
        session_id: response.session_id,
      });

      addChatItem(filterInvalidChatItems(items));

      processCommands(getCommandsFromChatItems<ChatCommand>(items));
    },

    addChatItem(chat_items) {
      set((state) => {
        let item: ChatItem | undefined = undefined;

        if (Array.isArray(chat_items)) {
          item = chat_items[0];
          state.chat_items = appendArray(state.chat_items, chat_items);
        } else {
          item = chat_items;
          state.chat_items = append(state.chat_items, chat_items);
        }

        if (isOutputChatItem(item)) {
          useStateStore.getState().speakIfEnabled(item.data.text);
        }
      });
    },

    processCommands(commands) {
      if (!commands) return;

      for (const command of commands) {
        switch (command.type) {
          case 'follow_up_input':
            createFollowUpCommandHandler({
              sendMessage: get().sendBackgroundMessage,
              sendLabel: get().sendLabeledMessage,
            })(command);
            break;

          case 'close_focused_chat':
            handleClosedFocusedChatCommand({ cmd: command });
            break;
        }
      }
    },

    setUser(user) {
      set({ user });
    },

    selectOption(option, chat_item_index, context) {
      let send_message = false;

      set((state) => {
        // Get the chat item from provided index
        const chat_item = state.chat_items[chat_item_index];

        if (!isOutputChatItem(chat_item) || chat_item.data.disabled) {
          return;
        }

        const { data } = chat_item;

        let opt: ChatResponseOption | undefined;

        if (data.multiple_choice_options) {
          // If using multiselect, find item based on state variable
          opt = data.options?.find(
            (o) =>
              o.input.state_variable === option.input.state_variable &&
              o.input.value === option.input.value
          );
        } else {
          // If not multiselect, find item by value
          opt = data.options?.find((o) => o.input.value === option.input.value);
        }

        if (!opt) return state;

        /** Select the option */
        opt.selected = !opt.selected;

        /** Disable the item if not multiple choice */
        if (!chat_item.data.multiple_choice_options) {
          data.disabled = true;
          send_message = true;
        }
      });

      // The message was not multiple choice, so send the value of the option as a message
      if (send_message) {
        get().sendBackgroundMessage(option.input.value, context);
      }
    },

    async doneMultiselect(message, index, context) {
      const { chat_items, user, sendMessage } = get();

      if (!user) return;

      const item = chat_items[index];
      if (!isOutputChatItem(item)) return;

      /** Disable the item */
      set((state) => {
        const item = state.chat_items[index];

        if (item) {
          item.data.disabled = true;
        }
      });

      // Assemble the updates from the options
      const updates = item.data.options
        // Just get the state variable options
        ?.filter((opt) => !!opt.input.state_variable && opt.selected)
        .map((opt) => ({
          operator: '=',
          var_name: opt.input.state_variable || '',
          value: formatOptionStateVariableValue(opt.input.value),
          value_type: 'primitive',
        })) as StateUpdate[] | undefined;

      // Post state variable updates
      updates &&
        (await updateStateVariables({
          username: getUserChatID(user),
          application_id: getUserChatAppID(user),
          updates,
        }));

      // Send the message
      sendMessage(message, { silent: true, context });
    },
    skipMultiselect(message, index, context) {
      /** Disable the item */
      set((state) => {
        const item = state.chat_items[index];

        if (isOutputChatItem(item)) {
          item.data.disabled = true;
        }
      });

      get().sendMessage(message, { silent: true, context });
    },

    show(params) {
      const { user, initial_prompt } = params;

      set({
        is_visible: true,
        user,
      });

      if (!initial_prompt) return;

      // Send a message with the initial prompt
      get().sendMessage(initial_prompt.text, {
        silent: true,
        message_type:
          initial_prompt.type === 'label' ? 'labeled_intent' : 'text',
      });
    },

    close(after_close_label) {
      // Clear the state and close the window
      set({
        is_visible: false,
        chat_items: [],
        user: undefined,
        session_id: undefined,
      });

      // Send a labelled intent on the main chat thread to signify that the chat window is closed
      useStateStore
        .getState()
        .sendChatBackgroundMessage(after_close_label || 'Reset_daily_survey');
    },

    onPressOtherOption(input, index) {
      get().sendBackgroundMessage(input);

      /** Disable the item that was selected */
      set((state) => {
        const item = state.chat_items[index];

        if (item) {
          item.data.disabled = true;
        }
      });
    },
  }))
);

type FocusedChatState = {
  chat_items: ChatItem[];
  session_id?: string;
  user?: UserModel;
  is_visible?: boolean;

  show(params: {
    user: UserModel;
    initial_prompt?: OpenFocusChatInitialPrompt;
  }): void;
  close(after_close_label?: string): void;

  setUser(user: UserModel): void;

  sendMessage(
    message: string,
    options?: { silent?: boolean; message_type?: ChatInputType; context?: any }
  ): Promise<void>;

  sendBackgroundMessage(message: string, context?: any): Promise<void>;

  sendLabeledMessage(message: string, context?: any): Promise<void>;

  processResponse(
    response: ChatApiResponse,
    options?: { direct_only?: boolean }
  ): void;

  addChatItem(chat_items: ChatItem | ChatItem[]): void;

  processCommands(commands?: ChatCommand[]): void;

  doneMultiselect(
    message: string,
    chat_item_index: number,
    context?: any
  ): Promise<void>;
  skipMultiselect(
    message: string,
    chat_item_index: number,
    context?: any
  ): void;

  selectOption(
    option: ChatResponseOption,
    chat_item_index: number,
    context?: any
  ): void;

  /** Currently just used in Geneva wheel */
  onPressOtherOption(input: string, index: number): void;

  // selectGenevaOption(params: SelectGenevaOptionParams): Promise<void>;
};

type OpenFocusChatInitialPrompt =
  | {
      type: 'text';
      text: string;
    }
  | {
      type: 'label';
      text: string;
    };

// type SelectGenevaOptionParams = {
//   option: ChatResponseOption;
//   value: string;
//   input: string;
//   chat_item_index: number;
//   state_variable: string;
// };

function formatOptionStateVariableValue(value: string) {
  if (value === 'True') return 'true';

  return value;
}
